<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";

import { cardKey } from "@/components/StickyNote/injectKeys";
import EnablingTeam from "@/components/StickyNote/internal/ActionMenu/internal/EnablingTeamMenuItem/EnablingTeam.vue";
import ActionSource from "@/components/action/ActionSource.vue";
import AttributeChip from "@/components/chip/AttributeChip.vue";
import MirrorStickyChip from "@/components/chip/MirrorStickyChip.vue";
import StickyLinksChip from "@/components/chip/StickyLinksChip.vue";
import SvgIcon from "@/components/ui/SvgIcon/SvgIcon.vue";
import type { Team } from "@/model/session";
import { getRouter } from "@/router";
import { useSessionStore } from "@/store/session";
import { injectStrict } from "@/utils/vue";

const card = injectStrict(cardKey);

const { t } = useI18n();

const openTeamBoardInNewTab = (team: Team) => {
  const currentSession = useSessionStore().current.name;

  const route = getRouter().resolve({
    name: "app",
    params: { session: currentSession, team: team.name, board: "team" },
    query: { highlightStickyId: card.groupId ?? card.id },
  });

  const fullUrl = window.location.origin + window.location.pathname + "#" + route.fullPath;
  window.open(fullUrl);
};

const dependTeam = computed(() =>
  card.dependTeam ? card.dependTeam.name : t("actionMenu.team.unassigned"),
);

const triggerAriaLabel = computed(() =>
  t("actionMenu.enabling.ariaLabel", { team: dependTeam.value }),
);
</script>

<template>
  <div class="dependency-footer">
    <ActionSource value="attribute-chip" class="row">
      <StickyLinksChip :linking-card="{ type: 'sticky', id: card.id }" show-upper-level-links />
    </ActionSource>
    <ActionSource value="attribute-chip" class="row">
      <div v-if="card.precondTeam" class="team-chip-wrapper">
        <AttributeChip
          class="team-chip"
          name="precond-team-chip"
          :text="card.precondTeam.name"
          :tooltip="$t('dependency.goToTeamBoard', { team: card.precondTeam.name })"
          shrink
          @click="openTeamBoardInNewTab(card.precondTeam)"
        />
      </div>

      <SvgIcon name="arrow/right" class="icon" width="20" height="100%" />

      <div class="team-chip-wrapper">
        <AttributeChip
          v-if="card.dependTeam"
          class="team-chip"
          name="depend-team-chip"
          :text="card.dependTeam.name"
          :tooltip="$t('dependency.goToTeamBoard', { team: card.dependTeam.name })"
          shrink
          @click="openTeamBoardInNewTab(card.dependTeam)"
        />
        <AttributeChip
          v-else
          name="depend-on-team-chip"
          :text-placeholder="$t('actionMenu.enabling')"
          :tooltip="$t('actionMenu.enabling')"
          :dropdown-props="{ width: 'md' }"
          :trigger-aria-label
        >
          <EnablingTeam />
        </AttributeChip>
      </div>

      <span v-if="card.dependTeam" class="mirror">
        <MirrorStickyChip>
          <template #prefix>
            <SvgIcon name="arrow/right" class="icon" width="20" height="100%" />
          </template>
        </MirrorStickyChip>
      </span>
    </ActionSource>
  </div>
</template>

<style lang="scss" scoped>
@use "@/styles/mixins/typography";

.dependency-footer {
  display: flex;
  flex-direction: column;
  row-gap: 8px;

  .row {
    display: flex;
    align-items: center;
    height: 20px;
    gap: 6px;

    > * {
      min-width: 0;
    }

    > .icon {
      flex-shrink: 0;
    }

    .team-chip-wrapper {
      flex: 0 1 auto;
      min-width: 50px;
      display: flex;

      :deep(.dropdown-menu) {
        max-width: 100%;
      }
    }

    .mirror {
      display: flex;
      align-items: center;
      gap: 6px;
      flex-shrink: 0;
      margin-left: auto;
    }
  }
}
</style>
